import { FormActionsMui } from '@app/components/form/actions/FormActionsMui';
import { Page } from '@app/components/page/Page';
import { FragmentIcon } from '@app/modules/fragments/components/FragmentIcon';
import {
  EnrichedVideoEditorEditListItemDto,
  GetFragmentDto,
} from '@app/modules/fragments/fragment.types';
import { SOCIAL_EXPORT_FRAGMENT_SOURCE } from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import { PublishConnectionSelector } from '@app/modules/social-export/components/PublishConnectionSelector';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useMediaLimitsIndication } from '@app/modules/social-export/hooks/useMediaLimitsIndication';
import { fragmentToSocialExportPublishSource } from '@app/modules/social-export/lib/social-export-publish-source';
import {
  MediaLimitsAlert,
  MediaSelector,
  NoSourcesAlert,
  VideoPreview,
  publishMessages,
} from '@app/modules/social-export/publish';
import { SocialExportGetStationConnectionStateDto } from '@mi-front/api-v2';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, CircularProgress, Grid } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { defineMessage, useIntl } from 'react-intl';

import { type SocialExportPublishForm } from './useSocialExportUploadPage';

const publishLabelMessage = defineMessage({
  id: 'socialExport.form.publish',
  defaultMessage: 'Publicar',
});

export interface SocialExportUploadPageContentProps {
  fragment: GetFragmentDto;
  fragmentMediaUrl: string;
  /** Connection settings for the selected account; loading only affects the publish form. */
  settingsQuery: UseQueryResult<unknown, unknown>;
  platform: SocialPlatform;
  selectedSource: string;
  onSelectedSourceChange: (value: string) => void;
  edits: EnrichedVideoEditorEditListItemDto[];
  isFragmentSourceAllowed: boolean;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedEdit: boolean;
  renderedVideoUrl: string | null;
  PublishForm: SocialExportPublishForm;
  onUpload: (data: Record<string, unknown>) => Promise<void>;
  connections: SocialExportGetStationConnectionStateDto[];
  selectedConnectionId: string | null;
  onSelectedConnectionChange: (id: string | null) => void;
}

/** Layout and conditional UI once fragment, media, and edits are loaded. */
export function SocialExportUploadPageContent({
  fragment,
  fragmentMediaUrl,
  settingsQuery,
  platform,
  selectedSource,
  onSelectedSourceChange,
  edits,
  isFragmentSourceAllowed,
  hasAvailableSources,
  showMediaSelector,
  hasSelectedRenderedEdit,
  renderedVideoUrl,
  PublishForm,
  onUpload,
  connections,
  selectedConnectionId,
  onSelectedConnectionChange,
}: SocialExportUploadPageContentProps) {
  const { $t } = useIntl();
  const publishSource = fragmentToSocialExportPublishSource(fragment);

  const effectiveMediaUrl =
    selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE
      ? isFragmentSourceAllowed
        ? (fragmentMediaUrl ?? null)
        : null
      : renderedVideoUrl;

  const canPublish =
    effectiveMediaUrl !== null &&
    ((selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE && isFragmentSourceAllowed) ||
      hasSelectedRenderedEdit);

  const title = $t(publishMessages.publishToPlatform, { platformName: platform.name });
  const fragmentLabel = fragment.title ?? fragment.id;
  const areSettingsReady = !settingsQuery.isLoading && settingsQuery.data !== undefined;

  const selectedConnection = useMemo(
    () => connections.find((c) => c.id === selectedConnectionId),
    [connections, selectedConnectionId],
  );
  // Duration is known for the fragment source; edit renders do not expose duration on list DTOs.
  const durationMs =
    selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE
      ? fragment.chunks.reduce((sum, chunk) => sum + chunk.duration, 0)
      : undefined;
  const mediaLimitsIndication = useMediaLimitsIndication(selectedConnection, durationMs);

  return (
    <SocialExportContext.Provider value={{ mediaUrl: effectiveMediaUrl ?? '' }}>
      <Page htmlTitle={title}>
        <Page.Header
          icon={platform.Icon()}
          title={title}
          breadcrumbs={
            <Page.Breadcrumbs
              items={[
                {
                  icon: <FragmentIcon fontSize="inherit" />,
                  content: fragmentLabel,
                },
              ]}
            />
          }
        />
        <Page.Content maxWidth="lg">
          {!hasAvailableSources ? (
            <NoSourcesAlert
              description={publishMessages.panoramaNoSourcesFragment}
              linkTo={`/fragments/${fragment.id}/edits`}
            />
          ) : (
            <>
              {showMediaSelector && (
                <Box sx={{ mb: 4, mt: 2 }}>
                  <MediaSelector
                    value={selectedSource}
                    onChange={onSelectedSourceChange}
                    edits={edits}
                    includeFragmentOption={isFragmentSourceAllowed}
                  />
                </Box>
              )}
              {!canPublish && selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE
                ? $t(publishMessages.mediaSelectorRenderNotReadyFragment)
                : effectiveMediaUrl && (
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={7}>
                        <PublishConnectionSelector
                          connections={connections}
                          selectedConnectionId={selectedConnectionId}
                          onSelectedConnectionChange={onSelectedConnectionChange}
                          platform={platform}
                        />
                        {mediaLimitsIndication ? (
                          <MediaLimitsAlert indication={mediaLimitsIndication} />
                        ) : null}
                        {areSettingsReady ? (
                          <PublishForm
                            key={selectedConnectionId ?? undefined}
                            source={publishSource}
                            videoUrl={effectiveMediaUrl}
                            onUpload={onUpload}
                            defaultSettings={settingsQuery.data}
                            connectionId={selectedConnectionId ?? ''}
                            actions={
                              <FormActionsMui
                                separator
                                saveIcon={<UploadIcon />}
                                saveLabel={$t(publishLabelMessage)}
                                disableSave={mediaLimitsIndication?.blocksPublish}
                                submitDisabledTooltip={
                                  mediaLimitsIndication?.blocksPublish
                                    ? mediaLimitsIndication.message
                                    : undefined
                                }
                                cancel
                              />
                            }
                          />
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <VideoPreview videoUrl={effectiveMediaUrl} />
                      </Grid>
                    </Grid>
                  )}
            </>
          )}
        </Page.Content>
      </Page>
    </SocialExportContext.Provider>
  );
}
